import { useState } from "react";

import {
  toDate,
  toRelativeDate,
  isSameDate,
  toISODateTime,
  toBrowserLocalDay,
} from "common/utils/dates";
import { Feature, UserSubscriptionAction } from "common/types";

import Alert from "client/common/components/content/Alert";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Calendar from "client/common/components/calendars/Calendar";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useFeatures } from "client/clinics/hooks";

import { useSubscriptionQuery, useUpdateSubscriptionMutation } from "../../hooks";
import {
  doesSubscriptionRequireAction,
  getRetentionOfferDeadlineWarning,
  minSelectableDeliveryDate,
  minSelectableNextOrderDate,
} from "../../utils";
import { DetailSubscription } from "../../types";
import SubscriptionDateBanners from "../SubscriptionDateBanners";
import SubscriptionAlert from "../SubscriptionAlert";

import styles from "../../styles/SubscriptionNextOrderDateUpdateDrawer.module.less";

export type SubscriptionNextOrderDateUpdateDrawerProps = {
  id: number;
};

type SubscriptionNextOrderDateUpdateFormProps = {
  subscription: DetailSubscription;
};

const SubscriptionNextOrderDateUpdateForm = ({
  subscription,
}: SubscriptionNextOrderDateUpdateFormProps) => {
  const { close } = useCurrentDrawer();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];
  const {
    mutate: updateSubscription,
    isPending: isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  // v2 subscriptions are scheduled by expected delivery date; v1 by next order date.
  const currentDateSource = isEdbEnabled
    ? subscription.expected_delivery_by
    : subscription.next_order_date;
  const currentDate = currentDateSource ? new Date(currentDateSource) : new Date();
  const [scheduleDate, setScheduleDate] = useState<Date>(() => {
    if (isEdbEnabled && currentDate < minSelectableDeliveryDate()) {
      return minSelectableDeliveryDate();
    }
    return currentDate;
  });
  const isSameDateSelected = isSameDate(currentDate, scheduleDate);
  const retentionOfferDeadlineWarning = getRetentionOfferDeadlineWarning({
    offer: subscription.accepted_retention_offer,
    scheduleDate,
    isEdbEnabled,
  });

  const getDescription = () => {
    if (isEdbEnabled) {
      return subscription.expected_delivery_by
        ? `Use the calendar below to select your next estimated delivery date. Currently, your next estimated delivery is ${toDate(
            subscription.expected_delivery_by,
          )} (${toRelativeDate(subscription.expected_delivery_by)}).`
        : "Use the calendar below to select your next estimated delivery date.";
    }
    return subscription.next_order_date
      ? `Use the calendar below to select your next order date. Currently, your next order will be placed on ${toDate(
          subscription.next_order_date,
        )} (${toRelativeDate(subscription.next_order_date)}).`
      : "Use the calendar below to select your next order date.";
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title={isEdbEnabled ? "Update next delivery" : "Update next order"}
        description={getDescription()}
        onClose={close}
      />
      <DrawerLayout.Form
        submitText={isEdbEnabled ? "Update delivery" : "Update order"}
        canSubmit={!isSameDateSelected && !doesSubscriptionRequireAction(subscription)}
        onSubmit={() =>
          updateSubscription({
            action: UserSubscriptionAction.Modify,
            ...(isEdbEnabled
              ? { expected_delivery_by: toISODateTime(scheduleDate) }
              : { next_order_date: toISODateTime(scheduleDate) }),
          })
        }
        isLoading={isLoading}
        error={error}
        fieldErrors={[
          error?.errors.action,
          isEdbEnabled ? error?.errors.expected_delivery_by : error?.errors.next_order_date,
        ]}
      >
        {doesSubscriptionRequireAction(subscription) && (
          <DrawerLayout.Alerts>
            <SubscriptionAlert subscription={subscription} />
          </DrawerLayout.Alerts>
        )}
        {isEdbEnabled && (
          <div className={styles.disclaimer}>
            Earliest available delivery: {toDate(minSelectableDeliveryDate())}
          </div>
        )}
        <DrawerLayout.Form.Group>
          <Calendar
            disabledDays={{
              before: toBrowserLocalDay(
                isEdbEnabled ? minSelectableDeliveryDate() : minSelectableNextOrderDate(),
              ),
            }}
            selectedDays={toBrowserLocalDay(scheduleDate)}
            modifiers={{ previous: toBrowserLocalDay(currentDate) }}
            onSelect={setScheduleDate}
          />
        </DrawerLayout.Form.Group>
        {!isSameDateSelected && (
          <DrawerLayout.Form.Group>
            <DrawerLayout.Form.Label>
              {isEdbEnabled
                ? "Your next estimated delivery will now be:"
                : "Your next order date will now be:"}
            </DrawerLayout.Form.Label>
            <SubscriptionDateBanners dates={[scheduleDate]} />
            {retentionOfferDeadlineWarning && (
              <Alert variant="warning">{retentionOfferDeadlineWarning}</Alert>
            )}
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

const SubscriptionNextOrderDateUpdateDrawer = ({
  id,
}: SubscriptionNextOrderDateUpdateDrawerProps) => {
  const { data: subscription, isLoading, error } = useSubscriptionQuery(id);

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!subscription || error) {
    return <DrawerLayout.FetchError entityName="Autoship" error={error} />;
  }

  return <SubscriptionNextOrderDateUpdateForm subscription={subscription} />;
};

export default SubscriptionNextOrderDateUpdateDrawer;
